import { Component, ChangeDetectionStrategy, input, output, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import dayjs from 'dayjs/esm';
import { NewUpload, BaseTag, FileSizeFormatterPipe, PipesModule } from '@xmedical/core';
import { XmedicalUiModule } from '@xmedical/ui';
import { FileUploaderService } from '../../services/file-uploader.service';
import { FileUploadEventsService, UploadProgressEvent } from '../../services/file-upload-events.service';
import { UploadType } from '../../models/upload-type.type';

const MAX_UPLOAD_SIZE_GB = 5e9;

@Component({
  selector: 'xmed-file-upload-item',
  standalone: true,
  imports: [
    FormsModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    PipesModule,
    XmedicalUiModule
  ],
  providers: [],
  templateUrl: './file-upload-item.component.html',
  styleUrl: './file-upload-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadItemComponent implements OnInit, OnDestroy {
  private readonly uploaderService = inject(FileUploaderService);
  private readonly uploadEvents = inject(FileUploadEventsService);
  private readonly fileSizePipe = new FileSizeFormatterPipe();

  file = input.required<File>();
  index = input.required<number>();
  tags = input<string[]>([]);
  uploadType = input<UploadType>('practice');
  patientId = input<number | null>(null);
  commentId = input<number | null>(null);

  deleteFile = output<number>();

  uploadProgressPercentage = signal(0);
  uploadProgressSize = signal(0);
  uploading = signal(false);
  processing = signal(false);
  errorMessage = signal('');
  fileTags = signal<BaseTag[]>([]);
  documentDate: dayjs.Dayjs = dayjs();

  private fileKey = '';
  private uploadEventsSubscription: Subscription | null = null;

  fileExtension = computed(() => this.file().name.split('.').pop() || '');
  hasError = computed(() => this.errorMessage().length > 0);
  complete = computed(() => this.uploadProgressPercentage() === 100);

  searchTags = (query: string): Observable<BaseTag[]> => {
    return this.uploaderService.queryTags(query);
  };

  ngOnInit(): void {
    this.getFileKey();
    this.validateFileSize();
    this.uploadEventsSubscription = this.uploadEvents.uploadEvents$
      .pipe(filter(event => event.fileName === this.file().name))
      .subscribe(event => this.uploadEventHandler(event));

    const tagsInput = this.tags();
    if (tagsInput.length) {
      this.fileTags.set(tagsInput.map(tag => ({ id: 0, tag: tag, hexColor: '#ffffff' } as BaseTag)));
    }
  }

  ngOnDestroy(): void {
    this.uploadEventsSubscription?.unsubscribe();
  }

  onTagsChange(tags: BaseTag[]): void {
    this.fileTags.set(tags);
  }

  deleteAttachment(): void {
    this.deleteFile.emit(this.index());
  }

  startUpload(): void {
    if (this.hasError()) return;

    this.processing.set(true);
    this.uploadProgressPercentage.set(100);

    this.uploaderService.getSignedUploadsUrl(this.fileKey, 'PUT', 300).subscribe({
      next: (url) => {
        this.processing.set(false);
        this.uploading.set(true);
        this.uploadProgressPercentage.set(0);

        this.uploaderService.uploadFile(this.file(), url).subscribe({
          next: () => {
            this.uploaderService.saveUpload(this.generateUpload(), this.uploadType()).subscribe({
              next: () => {
                this.uploading.set(false);
              },
              error: (err) => {
                this.uploading.set(false);
                console.error('Failed to save upload:', err);
              }
            });
          },
          error: (err) => {
            this.processing.set(false);
            this.uploadProgressPercentage.set(0);
            console.error('Failed to upload file:', err);
          }
        });
      },
      error: (err) => {
        this.processing.set(false);
        this.uploadProgressPercentage.set(0);
        console.error('Failed to get signed URL:', err);
      }
    });
  }

  private validateFileSize(): void {
    const file = this.file();
    if (file.size > MAX_UPLOAD_SIZE_GB) {
      this.errorMessage.set(
        'Unfortunately this file size is too large. The maximum size for a single file is ' +
        this.fileSizePipe.transform(MAX_UPLOAD_SIZE_GB, false) +
        ' and your file is ' + this.fileSizePipe.transform(file.size, false)
      );
    }
  }

  private generateUpload(): NewUpload {
    return {
      patientId: this.patientId() ?? undefined,
      commentId: this.commentId() ?? undefined,
      tags: this.fileTags(),
      fileName: this.file().name,
      extension: this.file().name.split('.').pop() || '',
      path: this.fileKey,
      size: this.file().size,
      documentDate: this.formatDateOnly(this.documentDate)
    };
  }

  private formatDateOnly(date: dayjs.Dayjs): string {
    return date.format('YYYY-MM-DD');
  }

  private getFileKey(): void {
    const file = this.file();
    const extension = file.name.split('.').pop() || '';
    const name = this.uploaderService.generateUuid();
    if (this.uploadType() === 'patient') {
      this.fileKey = 'uploads/patients/files/' + encodeURI(name) + '.' + extension;
    } else {
      this.fileKey = 'uploads/practice/files/' + encodeURI(name) + '.' + extension;
    }
  }

  private uploadEventHandler(event: UploadProgressEvent): void {
    const file = this.file();
    if (event.type === 'complete') {
      this.uploadProgressPercentage.set(event.progress);
      this.uploadProgressSize.set(file.size);
      this.uploading.set(false);
      this.errorMessage.set('');
    }
    if (event.type === 'error') {
      this.uploading.set(false);
      this.errorMessage.set(event.error || 'Unknown error');
    }
    if (event.type === 'progress') {
      this.uploadProgressPercentage.set(event.progress);
      this.uploadProgressSize.set(file.size * (event.progress / 100));
    }
  }
}
